import moment from 'moment-timezone';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {PagedModel} from '@shared/api/model/paged-model';
import {BaseService} from '@shared/services/api/base-service';
import {DeviceObjectSearchTerm, DeviceObjectsSearchScopeOptions, DeviceSearchTerm, DevicesSearchScopeOptions} from './bacer-devices.types';
import {Collector, Device, DeviceObject, DeviceObjectPrediction, Equipment, Favourite, Long} from '@app/graphql-typings/graphql';
import * as gql from 'gql-query-builder';
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {ClassTransformer, Transform, Type} from 'class-transformer';
import {Moment} from 'moment-timezone';
import {CollectorImpl} from '@shared/services/api/collectors.service';
import {FavouriteImpl} from '@app/layout/sitemanager/collectors-favourites/favourites.service';
import {PageInfo} from '@shared/components/ace-table/ace-table.component';

class DeviceImpl implements Device {
  bacnet_device_id?: Long /* BACNet device ID. */;
  bacnet_device_name?: string /* BACNet device name. */;
  canonical_favourite_ids?:
    | Long[]
    | null /* List of Favourites belonging to this Device. */;
  collector_id?: Long;
  @Type(() => CollectorImpl)
  collector?: Collector /* Collector object for current Device. */;
  comment?: string /* Comments. */;
  device_id: Long /* Unique identifier for a BACNet device. */;
  device_label?: string /* Device label. */;
  is_active?: boolean /* Is Device active? */;
  model?: string | null /* Device Model. */;
  vendor?: string | null /* Device Vendor. */;
  primary_equipment?: Equipment[];
  @Type(() => DeviceObjectImpl)
  device_objects?: DeviceObject[];
  updated_at?: Moment /* Timestamp Device was last updated. */;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  discovered_at?: Moment /* Timestamp Device was first discovered. */;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at?: Moment;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  lastseen_at?: Moment/* Timestamp Device was last seen. */;
}

export class DeviceObjectImpl implements DeviceObject {
  device_object_id?: Long;
  device_id?: number;
  object_name?: string;
  object_identifier?: string;
  description?: string;
  present_value?: number;
  units?: string;
  @Type(() => DeviceImpl)
  device?: Device;
  @Type(() => FavouriteImpl)
  favourite?: Favourite;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at?: Moment /* Timestamp Device was last updated. */;
  predictions?: DeviceObjectPrediction;
}


@Injectable()
export class BacerDevicesService extends BaseService {
  public static readonly modelMapping = {Device: DeviceImpl, DeviceObject: DeviceObjectImpl};

  private readonly DEFAULT_PAGE_SIZE = 500;
  private readonly LIMITED_PAGE_SIZE = 50;
  private readonly logger: AceLogger;

  constructor(private http: HttpClient,
              private classTransformer: ClassTransformer,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(BacerDevicesService.name);
  }

  searchDevices(pageInfo: PageInfo,
                searchTerm: DeviceSearchTerm,
                scopeOptions: DevicesSearchScopeOptions = {getPrimaryEquipment: false, getDeviceObjects: false}): Observable<PagedModel<Device[]>> {
    const query = gql.query({
      operation: 'devices',
      fields: ['bacnet_device_id', 'bacnet_device_name', 'device_id', 'device_label', 'lastseen_at', 'model', 'vendor', 'discovered_at', 'collector_id',
        ...scopeOptions.getDeviceObjects ? [
          {device_objects: ['device_object_id', 'object_identifier', 'object_name', 'units']}
        ] : [],
        ...scopeOptions.getPrimaryEquipment ? [
          {primary_equipment: ['primary_device_id', 'favourite_ids', 'canonical_favourite_ids', 'equipment_id', 'name', 'zone_id', 'metadata_type_id',
              { metadata_type: ['type_id', 'type'] },
              { zone: ['zone_id', {level: ['level_name']}, {zone_name: ['zone_name']}]},
              {favourites: ['fav_id', 'device_object_id', 'zone_id', 'canonical_equipment_id', 'identifier',
                  { zone: [{level: ['level_name']}, {zone_name: ['zone_name']}]},
                  {device_object: ['device_object_id', 'object_identifier', 'object_name', 'units', 'device_id',
                      { device: ['bacnet_device_name', 'bacnet_device_id', 'device_id'] }]},
                  {metadata: ['metadata_id', 'type_id', 'code', 'name']},
                ]}]}
        ] : []],
      variables: {
        site_id: {type: GRAPHQL_TYPES.INTEGER},
        collector_id: {type: GRAPHQL_TYPES.INTEGER },
        device_id: {type: GRAPHQL_TYPES.LONG},
        bacnet_device_id: {type: GRAPHQL_TYPES.INTEGER},
        bacnet_device_name: {type: GRAPHQL_TYPES.STRING},
        vendor: {type: GRAPHQL_TYPES.STRING},
        model: {type: GRAPHQL_TYPES.STRING},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true},
        is_primary_device: {type: GRAPHQL_TYPES.BOOLEAN}
      }
    });

    if (searchTerm.model && searchTerm.modelExact) {
      throw new Error('model and modelExact search terms are are mutually exclusive but are both defined');
    } else if (searchTerm.vendor && searchTerm.vendorExact) {
      throw new Error('vendor and vendorExact search terms are are mutually exclusive but are both defined');
    }
    const vendor = searchTerm.vendor ? '%' + searchTerm.vendor + '%' : searchTerm.vendorExact ? searchTerm.vendorExact : null;
    const model = searchTerm.model ? '%' + searchTerm.model + '%' : searchTerm.modelExact ? searchTerm.modelExact : null;

    delete searchTerm.vendorExact;
    delete searchTerm.modelExact;

    const variables = {...searchTerm,
      ...{start_index: pageInfo.first, limit: pageInfo.rows},
      ...vendor && {vendor: vendor},
      ...model && {model: model}};

    return this.httpUtilityService.graphQL<any[]>(query, 'devices', variables)
      .pipe(map(result => {
        const modelType = BacerDevicesService.modelMapping['Device'];
        return {
          data: this.classTransformer.plainToInstance(modelType, result.data),
          count: result.count
        };
      }));
  }

  getAllDevices(searchTerm: DeviceSearchTerm, scopeOptions?: DevicesSearchScopeOptions, progressCallback?: (a: number) => void): Observable<Device[]> {
    let completedSoFar = 0;
    const devicesSearchPageWrapper = (pageInfo: PageInfo) => this.searchDevices(pageInfo, searchTerm, scopeOptions)
      .pipe(tap((result: PagedModel<Device[]>) => {
        if (typeof progressCallback === 'function') {
          completedSoFar += result.data.length;
          const percentageSoFar = (completedSoFar/result.count) * 100;
          progressCallback(percentageSoFar);
        }
    }));
    /* Limit the page size if getPrimaryEquipment or getDeviceObjects is true as we need to
       ensure the response stays below the AWS gateway size limit of 10MB */
    const pageSize = scopeOptions?.getPrimaryEquipment ? this.LIMITED_PAGE_SIZE : this.DEFAULT_PAGE_SIZE;
    return this.httpUtilityService.pageUntilComplete(devicesSearchPageWrapper, {first: 0, rows: pageSize});
  }

  getDeviceObjects(pageInfo: PageInfo,
                   searchTerm?: DeviceObjectSearchTerm,
                   scopeOptions: DeviceObjectsSearchScopeOptions = {getFavouritesData: false, getDevice: false, getPredictions: false}): Observable<PagedModel<DeviceObject[]>> {
    const query = gql.query({
      operation: 'device_objects',
      fields: ['device_object_id', 'device_id', 'object_identifier', 'object_name', 'description', 'present_value', 'last_modified_at', 'units',
        ...(scopeOptions.getDevice || scopeOptions.getFavouritesData) ? [
          {device: ['collector_id', 'bacnet_device_name', 'bacnet_device_id', 'device_id', 'lastseen_at']}
        ] : [],
        ...scopeOptions.getFavouritesData ? [
          {favourite: ['fav_id', 'zone_id', 'comment', 'equipment_id', 'canonical_equipment_id', 'metadata_id', 'device_object_id',
              { zone: [{level: ['level_name']}, {zone_name: ['zone_name']}]},
              {equipment: ['metadata_type_id', 'virtual', 'shared', 'equipment_id', 'name', { metadata_type: ['type_id', 'type' ] } ]},
              {metadata: ['metadata_id', 'code', 'name', 'type_id', {unit: ['unit']}]},
            ]
          }
        ] : [],
        ...scopeOptions.getPredictions ? [
          {predictions: [{metadata_predictions: ['metadata_id', 'score']}]}
        ] : []
      ],
      variables: {
        site_id: {type: GRAPHQL_TYPES.INTEGER},
        collector_id: {type: GRAPHQL_TYPES.INTEGER},
        equipment_id: {type: GRAPHQL_TYPES.LONG},
        metadata_id: {type: GRAPHQL_TYPES.INTEGER},
        zone_id: {type: GRAPHQL_TYPES.INTEGER},
        device_id: {type: GRAPHQL_TYPES.LONG},
        object_identifier: {type: GRAPHQL_TYPES.STRING},
        unit: {type: GRAPHQL_TYPES.STRING},
        has_present_value: {type: GRAPHQL_TYPES.BOOLEAN},
        filter: {type: GRAPHQL_TYPES.STRING},
        object_name: {type: GRAPHQL_TYPES.STRING},
        is_favourite: {type: GRAPHQL_TYPES.BOOLEAN},
        bacnet_device_id: {type: GRAPHQL_TYPES.INTEGER},
        bacnet_device_name: {type: GRAPHQL_TYPES.STRING},
        device_object_id: {type: GRAPHQL_TYPES.LONG},
        device_object_ids: {type: GRAPHQL_TYPES.LONG, list: true},
        metadata_prediction_metadata_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    });

    const variables = Object.assign({start_index: pageInfo.first, limit: pageInfo.rows},
                                    searchTerm,
                                    {...searchTerm.filter && {filter: '%' + searchTerm.filter + '%'}});
    return this.httpUtilityService.graphQL<any[]>(query, 'device_objects', variables)
      .pipe(map(result => {
        const modelType = BacerDevicesService.modelMapping['DeviceObject'];
        return {
          data: this.classTransformer.plainToInstance(modelType, result.data),
          count: result.count
        };
      }));
  }

  getAllDeviceObjects(searchTerm?: DeviceObjectSearchTerm,
                      scopeOptions: DeviceObjectsSearchScopeOptions = {getFavouritesData: false, getDevice: false, getPredictions: false}): Observable<DeviceObject[]> {
    return this.httpUtilityService.pageUntilComplete(pageInfo => this.getDeviceObjects(pageInfo, searchTerm, scopeOptions), {first: 0, rows: this.DEFAULT_PAGE_SIZE});
  }

}
